import { ErrorOutline, InfoOutlined } from '@mui/icons-material'
import { Components, Theme } from '@mui/material'

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    'futureTheme.neutral': true
    'futureTheme.error-strong': true
    // TODO - uncomment when error severity removed
    // error: false
  }

  // TODO - uncomment when migration complete
  //   interface AlertPropsVariantOverrides {
  //   muted: false
  //   filled: false
  // }
}

// Text buttons in the action get custom styles per severity
const actionButtonSelector = '& .MuiAlert-action .MuiButton-root.MuiButton-text.MuiButton-colorSecondary'

const CONTENT_HEIGHT = '28px'

export const MuiAlert: Components<Omit<Theme, 'components'>>['MuiAlert'] = {
  defaultProps: {
    // The custom severities have no MUI default icon; the built-in severities
    // still fall through to MUI's defaultIconMapping per-severity
    iconMapping: {
      // TODO - proper icons
      'futureTheme.neutral': <InfoOutlined fontSize="inherit" />,
      'futureTheme.error-strong': <ErrorOutline fontSize="inherit" />,
    },
    slotProps: {
      // Most actions are centred, but override for the close button
      closeButton: {
        sx: {
          alignSelf: 'flex-start',
        },
      },
    },
  },
  styleOverrides: {
    icon: ({ theme }) => ({
      marginRight: theme.spacing(2.5),
      padding: 0,
      paddingTop: theme.spacing(0.5),
      minHeight: CONTENT_HEIGHT,
    }),
    action: ({ theme }) => ({
      marginRight: 0,
      // Ensure close action is top aligned without spacing
      paddingTop: theme.spacing(0),
      // Most actions are centred, close button is overridden above
      alignItems: 'center',
    }),
    message: ({ theme }) => ({
      padding: 0,
      alignSelf: 'center',
      '&:has(.MuiAlertTitle-root)': {
        ...theme.typography['futureTheme.small-regular'],
      },
      '& .MuiAlertTitle-root': {
        ...theme.typography['futureTheme.body-medium'],
      },
    }),
    root: ({ theme }) => ({
      padding: theme.spacing(2, 2, 2, 4),
      '&:not(:has(.MuiAlert-action))': {
        paddingRight: theme.spacing(4),
      },
      borderRadius: theme.shape['futureTheme.radius-lg'],
      borderStyle: 'solid',
      borderWidth: theme.futureTheme.strokeWidth.default,
      ...theme.typography['futureTheme.body-medium'],

      '& pre': {
        padding: theme.spacing(2.5, 4),
        backgroundColor: theme.palette.futureTheme.bg.surface.inset,
        borderRadius: theme.shape['futureTheme.radius-md'],
        margin: 0,
        marginTop: theme.spacing(1),
        whiteSpace: 'pre-wrap',
      },
    }),
  },
  variants: [
    {
      props: { severity: 'futureTheme.neutral' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
        color: theme.palette.futureTheme.text.default,
        borderColor: theme.palette.futureTheme.border.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.default,
        },
        // neutral severity button just uses standard secondary text
      }),
    },
    {
      props: { severity: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.info.muted,
        color: theme.palette.futureTheme.text.info.onInfoMuted,
        borderColor: theme.palette.futureTheme.border.info.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.info.onInfoMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.futureTheme.text.info.onInfoMuted,
        },
      }),
    },
    {
      props: { severity: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.success.muted,
        color: theme.palette.futureTheme.text.success.onSuccessMuted,
        borderColor: theme.palette.futureTheme.border.success.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.success.onSuccessMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.futureTheme.text.success.onSuccessMuted,
        },
      }),
    },
    {
      props: { severity: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.warning.muted,
        color: theme.palette.futureTheme.text.warning.onWarningMuted,
        borderColor: theme.palette.futureTheme.border.warning.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.warning.onWarningMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.futureTheme.text.warning.onWarningMuted,
        },
      }),
    },
    {
      props: { severity: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.danger.muted,
        color: theme.palette.futureTheme.text.danger.onDangerMuted,
        borderColor: theme.palette.futureTheme.border.danger.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.danger.onDangerMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
        },
      }),
    },
    {
      props: { severity: 'futureTheme.error-strong' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.danger.default,
        color: theme.palette.futureTheme.text.danger.onDanger,
        borderColor: theme.palette.futureTheme.bg.danger.default,
        '& .MuiAlert-icon': {
          color: theme.palette.futureTheme.fg.danger.onDanger,
        },
        [actionButtonSelector]: {
          color: theme.palette.futureTheme.text.danger.onDanger,
        },
      }),
    },
  ],
}
